const SCROLL_KEYFRAMES_GSAP_WAIT_FRAMES = 600;
const SCROLL_KEYFRAMES_MOBILE_CACHE_LIMIT = 10;
const SCROLL_KEYFRAMES_MOBILE_LOAD_CONCURRENCY = 2;
const SCROLL_KEYFRAMES_DESKTOP_LOAD_CONCURRENCY = 2;
const SCROLL_KEYFRAMES_DESKTOP_RENDER_PIXEL_LIMIT = 2560 * 1440;
const SCROLL_KEYFRAMES_DESKTOP_MAX_CACHE_LIMIT = 8;
const SCROLL_KEYFRAMES_DESKTOP_RETENTION_LIMIT = 16;
const SCROLL_KEYFRAMES_DESKTOP_CACHE_BYTES = 192 * 1024 * 1024;
const SCROLL_KEYFRAMES_LOAD_TIMEOUT = 15000;
const SCROLL_KEYFRAMES_RETRY_LIMIT = 3;

class ScrollKeyframes extends HTMLElement {
  #abortController;
  #canvas;
  #context;
  #diagnosticTimer;
  #frameCount = 1;
  #frameIndex = 0;
  #frameUrl;
  #frameSourceWidth = 0;
  #frameDirection = 1;
  #windowStart = 0;
  #windowEnd = 0;
  #images = new Map();
  #isMobile = false;
  #isTabletPortrait = false;
  #lastDrawnFrame = -1;
  #lastDrawnResource;
  #lastRequestedFrame = -1;
  #lastFrameRequestAt = 0;
  #lastCallout;
  #activeLoads = 0;
  #activeDownloads = 0;
  #activeDecodes = new Set();
  #decodeQueue = [];
  #loadGeneration = 0;
  #loadQueue = [];
  #loadTasks = new Map();
  #matchMedia;
  #outputHeight = 1;
  #outputScale = 1;
  #outputWidth = 1;
  #renderFrame;
  #renderTicker;
  #renderPixelLimit = SCROLL_KEYFRAMES_DESKTOP_RENDER_PIXEL_LIMIT;
  #resolvedCacheLimit = SCROLL_KEYFRAMES_DESKTOP_MAX_CACHE_LIMIT;
  #resizeObserver;
  #setupFrame;
  #stageHeight = 0;
  #timeline;
  #motionGeneration = 0;
  #prefetchIdleCallback;
  #prefetchedFrames = new Set();
  #prefetchRetryFrames = new Set();
  #loadFailures = new Map();
  #retryAfter = new Map();
  #prefetchTimer;
  #prefetchCursor = 0;
  #viewportWidth = 0;
  #waitAttempts = 0;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    this.#waitAttempts = 0;
    this.#canvas = this.querySelector('.scroll-keyframes__canvas');
    this.#context = this.#canvas?.getContext('2d', { alpha: false, desynchronized: true });

    if (!this.#canvas || !this.#context) return;

    this.#resizeObserver = new ResizeObserver(this.#handleCanvasResize);
    this.#resizeObserver.observe(this);
    this.#viewportWidth = window.innerWidth;
    window.addEventListener('resize', this.#handleViewportResize, {
      signal: this.#abortController.signal,
      passive: true
    });
    this.#waitForGsap();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    if (this.#setupFrame) cancelAnimationFrame(this.#setupFrame);
    if (this.#renderFrame) cancelAnimationFrame(this.#renderFrame);
    this.#renderTicker?.remove(this.#flushRender);
    this.#renderTicker = undefined;
    this.#setupFrame = undefined;
    this.#renderFrame = undefined;
    if (this.#diagnosticTimer) window.clearTimeout(this.#diagnosticTimer);
    this.#diagnosticTimer = undefined;
    this.#destroyMotion();
    this.#resetFrameLoader();
  }

  #waitForGsap = () => {
    if (!this.#abortController || this.#abortController.signal.aborted) return;

    if (window.gsap && window.ScrollTrigger) {
      this.#setupFrame = undefined;
      this.#setupMotion();
      return;
    }

    this.#waitAttempts += 1;
    if (this.#waitAttempts >= SCROLL_KEYFRAMES_GSAP_WAIT_FRAMES) return;

    this.#setupFrame = requestAnimationFrame(this.#waitForGsap);
  };

  #setupMotion() {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const stage = this.querySelector('.scroll-keyframes__stage');
    const awd = this.querySelector('[data-keyframe-callout="awd"]');
    const suspension = this.querySelector('[data-keyframe-callout="suspension"]');

    if (!stage || !awd || !suspension) return;

    gsap.registerPlugin(ScrollTrigger);
    this.#matchMedia = gsap.matchMedia();
    this.classList.add('is-enhanced');

    this.#matchMedia.add(
      {
        isDesktop: '(min-width: 1280px)',
        isTablet: '(min-width: 768px) and (max-width: 1279px)',
        isMobile: '(max-width: 767px)',
        reduceMotion: '(prefers-reduced-motion: reduce)'
      },
      (context) => {
        const { isDesktop, isMobile, reduceMotion } = context.conditions;
        const generation = ++this.#motionGeneration;
        let disposed = false;
        this.#configureFrames(Boolean(isMobile), Boolean(reduceMotion));

        if (reduceMotion) {
          this.classList.add('is-reduced-motion');
          this.#showStaticCallouts();
          return;
        }

        this.classList.remove('is-reduced-motion');
        const scrub = isDesktop ? 1 : isMobile ? 0.4 : 0.6;
        const distance = Number(this.dataset[isMobile ? 'scrollDistanceMobile' : 'scrollDistance']) || 250;
        const frameState = { frame: 0 };

        this.#prepareInitialFrames(generation).then(() => {
          if (disposed || generation !== this.#motionGeneration || !this.isConnected) return;

          this.#setActiveCallout(null);

          this.#timeline = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              id: `scroll-keyframes-${this.dataset.sectionId}`,
              trigger: this,
              start: 'top top',
              end: () => {
                const viewportHeight = isMobile ? this.#stageHeight : window.innerHeight;
                return `+=${Math.max(viewportHeight, (distance / 100) * viewportHeight)}`;
              },
              pin: stage,
              pinSpacing: true,
              scrub,
              anticipatePin: 1,
              onRefresh: (trigger) => {
                // Refresh can render the end state, then restore progress with
                // tween callbacks suppressed. Restore our non-GSAP state too.
                this.#requestFrame(Math.round(frameState.frame));
                this.#syncCallout(trigger.animation?.progress() || 0);
              },
              invalidateOnRefresh: true
            }
          });

          this.#timeline.to(frameState, {
            frame: this.#frameCount - 1,
            duration: 1,
            onUpdate: () => {
              this.#requestFrame(Math.round(frameState.frame));
              this.#syncCallout(frameState.frame / Math.max(1, this.#frameCount - 1));
            }
          }, 0);

          this.#requestScrollTriggerRefresh(ScrollTrigger);
        });

        return () => {
          disposed = true;
          this.#motionGeneration += 1;
          this.#timeline?.kill();
          this.#timeline = undefined;
          this.#setActiveCallout(null);
          this.#resetFrameLoader();
        };
      },
      this
    );
  }

  #configureFrames(isMobile, reduceMotion) {
    this.#resetFrameLoader();
    this.#isMobile = isMobile;
    this.#renderPixelLimit = SCROLL_KEYFRAMES_DESKTOP_RENDER_PIXEL_LIMIT;
    this.dataset.frameWorker = 'native-image';
    this.#viewportWidth = window.innerWidth;
    this.#setStableStageHeight();
    this.#syncCalloutLayout();
    this.#updateOutputMetrics();
    this.#frameCount = Math.max(1, Number(this.dataset[isMobile ? 'mobileFrameCount' : 'desktopFrameCount']) || 1);
    this.#frameUrl = this.dataset[isMobile ? 'mobileFirstFrame' : 'desktopFirstFrame'];
    this.#frameSourceWidth = this.#sourceWidthForOutput();
    this.#resolveCacheLimit();
    this.dataset.frameSourceWidth = String(this.#frameSourceWidth);
    this.#frameIndex = 0;
    this.#frameDirection = 1;
    this.#updateFrameWindow();
    this.#lastDrawnFrame = -1;
    this.dataset.framePerformanceTier = isMobile ? 'mobile' : 'quality';
    this.dataset.frameStep = '1';
    this.dataset.frameLoadConcurrency = String(this.#loadConcurrency);
    this.classList.remove('has-rendered-frame');
    if (!reduceMotion) this.#startProgressivePrefetch(this.#loadGeneration);
  }

  #handleViewportResize = () => {
    const viewportWidth = window.innerWidth;
    if (Math.abs(viewportWidth - this.#viewportWidth) <= 1) return;

    this.#viewportWidth = viewportWidth;
    this.#setStableStageHeight();
    this.#syncCalloutLayout();
    this.#updateOutputMetrics();
    this.#refreshFrameSource();
    this.#queueRender();
  };

  #handleCanvasResize = () => {
    this.#updateOutputMetrics();
    this.#refreshFrameSource();
    this.#queueRender();
  };

  #sourceWidthForOutput() {
    const sourceWidth = Number(this.dataset[this.#isMobile ? 'mobileFrameWidth' : 'desktopFrameWidth']);
    const sourceHeight = Number(this.dataset[this.#isMobile ? 'mobileFrameHeight' : 'desktopFrameHeight']);
    if (!(sourceWidth > 0 && sourceHeight > 0)) return 0;

    const coverWidth = Math.max(this.#outputWidth, this.#outputHeight * sourceWidth / sourceHeight);
    const requiredWidth = coverWidth * (this.#isTabletPortrait ? 0.72 : 1);
    const widths = this.#isMobile ? [720, 960, 1280, sourceWidth] : [1280, 1920, 2560, sourceWidth];
    // Allow for scrollbar/aspect rounding without selecting a whole larger tier.
    return Math.min(sourceWidth, widths.find((width) => width * 1.01 >= requiredWidth) || sourceWidth);
  }

  #refreshFrameSource() {
    if (!this.#frameUrl || this.classList.contains('is-reduced-motion')) return;
    const width = this.#sourceWidthForOutput();
    // Upgrade after a significant resize, but don't churn CDN variants on small
    // resize/address-bar changes. A breakpoint rebuild chooses a fresh tier.
    if (width <= this.#frameSourceWidth) return;
    this.#resetFrameLoader();
    this.#frameSourceWidth = width;
    this.#updateOutputMetrics();
    this.dataset.frameSourceWidth = String(width);
    this.dataset.frameWorker = 'native-image';
    this.dataset.framePerformanceTier = this.#isMobile ? 'mobile' : 'quality';
    this.#requestFrame(this.#frameIndex);
    this.#startProgressivePrefetch(this.#loadGeneration);
  }

  #syncCalloutLayout() {
    const callout = this.querySelector('[data-keyframe-callout="awd"]');
    const title = callout?.querySelector('.scroll-keyframes__callout-title');
    if (!callout || !title) return;

    if (!this.#isMobile) {
      callout.classList.remove('scroll-keyframes__callout--multiline');
      return;
    }

    const lineHeight = Number.parseFloat(getComputedStyle(title).lineHeight);
    const isMultiline = lineHeight > 0 && title.getBoundingClientRect().height > lineHeight * 1.5;
    callout.classList.toggle('scroll-keyframes__callout--multiline', isMultiline);
  }

  #setStableStageHeight() {
    if (!this.#isMobile) {
      this.#stageHeight = 0;
      this.style.removeProperty('--scroll-keyframes-mobile-stage-size');
      return;
    }

    this.#stageHeight = Math.max(1, Math.round(window.innerHeight));
    this.style.setProperty('--scroll-keyframes-mobile-stage-size', `${this.#stageHeight}px`);
  }

  async #prepareInitialFrames(generation) {
    const preloadCount = Math.min(this.#frameCount, this.#isMobile ? 5 : 6);
    const preloadTasks = Array.from(
      { length: preloadCount },
      (_, index) => this.#loadFrame(index, index === 0 ? 'high' : 'normal')
    );

    await Promise.allSettled(preloadTasks);
    if (generation !== this.#motionGeneration || !this.isConnected) return;

    this.#frameIndex = 0;
    this.#drawFrame();
  }

  #requestFrame(index) {
    const frameIndex = Math.min(this.#frameCount - 1, Math.max(0, Math.round(index)));
    const existingTask = this.#loadTasks.get(frameIndex);
    if (
      frameIndex === this.#lastRequestedFrame
      && (this.#images.has(frameIndex) || existingTask)
    ) return;

    if (frameIndex !== this.#lastRequestedFrame) {
      this.#frameDirection = this.#lastRequestedFrame < 0 || frameIndex > this.#lastRequestedFrame ? 1 : -1;
    }

    this.#frameIndex = frameIndex;
    this.#lastRequestedFrame = frameIndex;
    this.#lastFrameRequestAt = performance.now();
    this.#updateFrameWindow();
    this.#queueFrameDiagnostics();
    this.#cancelStaleImageLoads(this.#windowStart, this.#windowEnd);
    this.#pruneQueuedFrames(this.#windowStart, this.#windowEnd);
    this.#loadQueue.forEach((task) => {
      task.priority = task.index === frameIndex ? 'high' : task.prefetch ? 'low' : 'normal';
    });
    this.#sortLoadQueue();
    this.#loadFrame(frameIndex, 'high');
    this.#queueRender();

    for (let offset = 1; offset < this.#windowLimit; offset += 1) {
      const ahead = frameIndex + (offset * this.#frameDirection);
      const behind = frameIndex - (offset * this.#frameDirection);
      if (this.#isNearbyFrame(ahead)) this.#loadFrame(ahead, 'normal');
      if (this.#isNearbyFrame(behind)) this.#loadFrame(behind, 'normal');
    }

    this.#trimCache(frameIndex, this.#cacheLimit);
    this.#drainDecodeQueue();
  }

  #updateFrameWindow() {
    const trailingRadius = 2;
    const leadingRadius = this.#windowLimit - trailingRadius - 1;
    const start = this.#frameIndex - (this.#frameDirection > 0 ? trailingRadius : leadingRadius);
    this.#windowStart = Math.max(0, Math.min(start, this.#frameCount - this.#windowLimit));
    this.#windowEnd = Math.min(this.#frameCount - 1, this.#windowStart + this.#windowLimit - 1);
  }

  #isNearbyFrame(index) {
    return index >= this.#windowStart && index <= this.#windowEnd;
  }

  #pruneQueuedFrames(minimumFrame, maximumFrame) {
    const retainedTasks = [];
    let droppedLoads = 0;

    this.#loadQueue.forEach((task) => {
      const isStaleImage = !task.prefetch
        && (task.index < minimumFrame || task.index > maximumFrame);
      if (!isStaleImage) {
        retainedTasks.push(task);
        return;
      }

      task.cancelled = true;
      if (task.index < this.#prefetchCursor && !this.#prefetchedFrames.has(task.index)) {
        this.#prefetchRetryFrames.add(task.index);
      }
      if (this.#loadTasks.get(task.index) === task) this.#loadTasks.delete(task.index);
      task.resolve();
      droppedLoads += 1;
    });

    this.#loadQueue = retainedTasks;
    if (droppedLoads > 0) {
      const total = Number(this.dataset.frameDroppedLoads || 0) + droppedLoads;
      this.dataset.frameDroppedLoads = String(total);
      this.#updateLoadDiagnostics();
    }
  }

  #cancelStaleImageLoads(minimumFrame, maximumFrame) {
    let droppedLoads = 0;

    this.#loadTasks.forEach((task) => {
      if (
        !task.started
        || task.cancelled
        // Native decode is not reliably abortable. Let it settle, then discard
        // its result if it is still outside the latest bounded window.
        || task.decoding
        || (task.index >= minimumFrame && task.index <= maximumFrame)
      ) return;

      task.cancelled = true;
      if (task.index < this.#prefetchCursor && !this.#prefetchedFrames.has(task.index)) {
        this.#prefetchRetryFrames.add(task.index);
      }
      if (this.#loadTasks.get(task.index) === task) this.#loadTasks.delete(task.index);
      task.cancelImage?.();
      droppedLoads += 1;
    });

    if (droppedLoads > 0) this.#recordDroppedLoads(droppedLoads);
  }

  #recordDroppedLoads(count) {
    const total = Number(this.dataset.frameDroppedLoads || 0) + count;
    this.dataset.frameDroppedLoads = String(total);
  }

  #loadFrame(index, priority = 'normal', prefetch = false) {
    if (!this.#frameUrl || index < 0 || index >= this.#frameCount) return Promise.resolve();
    if (this.#images.has(index)) return Promise.resolve(this.#images.get(index));
    if (prefetch && this.#prefetchedFrames.has(index)) return Promise.resolve();
    if ((this.#loadFailures.get(index) || 0) >= SCROLL_KEYFRAMES_RETRY_LIMIT) return Promise.resolve();
    if (performance.now() < (this.#retryAfter.get(index) || 0)) return Promise.resolve();

    const existingTask = this.#loadTasks.get(index);
    if (existingTask) {
      if (!prefetch) existingTask.prefetch = false;
      if (this.#priorityRank(priority) > this.#priorityRank(existingTask.priority)) {
        existingTask.priority = priority;
        existingTask.setPriority?.(priority);
        this.#sortLoadQueue();
        this.#updateLoadDiagnostics();
      }
      return existingTask.promise;
    }

    const task = {
      cancelled: false,
      generation: this.#loadGeneration,
      index,
      prefetch,
      priority,
      started: false
    };
    task.promise = new Promise((resolve) => {
      task.resolve = resolve;
    });

    this.#loadTasks.set(index, task);
    this.#loadQueue.push(task);
    this.#sortLoadQueue();
    this.#updateLoadDiagnostics();
    this.#drainLoadQueue();
    return task.promise;
  }

  #priorityRank(priority) {
    if (priority === 'high') return 2;
    if (priority === 'normal') return 1;
    return 0;
  }

  #sortLoadQueue() {
    this.#loadQueue.sort((a, b) => (
      Number(b.index === this.#frameIndex) - Number(a.index === this.#frameIndex)
      || this.#priorityRank(b.priority) - this.#priorityRank(a.priority)
      || Math.abs(a.index - this.#frameIndex) - Math.abs(b.index - this.#frameIndex)
      || (b.index - a.index) * this.#frameDirection
    ));
  }

  #drainLoadQueue() {
    while (
      this.#activeDownloads < this.#loadConcurrency
      && this.#activeLoads < this.#windowLimit + 2
      && this.#loadQueue.length > 0
    ) {
      const task = this.#loadQueue.shift();
      if (!task || task.cancelled || task.generation !== this.#loadGeneration) {
        task?.resolve();
        continue;
      }

      task.started = true;
      this.#activeLoads += 1;
      this.#activeDownloads += 1;
      this.#updateLoadDiagnostics();
      this.#performLoad(task).then((resource) => {
        const isCurrent = !task.cancelled
          && task.generation === this.#loadGeneration
          && this.isConnected;

        if (isCurrent && resource) {
          this.#prefetchedFrames.add(task.index);
          this.#loadFailures.delete(task.index);
          this.#retryAfter.delete(task.index);
          if (resource.decoded && this.#isNearbyFrame(task.index)) {
            this.#images.set(task.index, resource);
            this.dataset.frameDecoder = resource.type;
            this.dataset.frameDecodedHeight = String(resource.height);
            this.dataset.frameDecodedWidth = String(resource.width);
            this.#trimCache(this.#frameIndex, this.#cacheLimit);
            // A nearer completed frame can improve the canvas while the exact
            // target is still loading. Rendering is coalesced on the GSAP ticker.
            this.#queueRender();
          } else {
            resource.close?.();
          }
          this.#updateCacheDiagnostics();
        } else {
          resource?.close?.();
          if (isCurrent) {
            this.#prefetchedFrames.delete(task.index);
            const failures = (this.#loadFailures.get(task.index) || 0) + 1;
            this.#loadFailures.set(task.index, failures);
            if (failures < SCROLL_KEYFRAMES_RETRY_LIMIT) {
              this.#retryAfter.set(task.index, performance.now() + 500 * (2 ** (failures - 1)));
              this.#prefetchRetryFrames.add(task.index);
              this.#schedulePrefetchBatch(this.#loadGeneration);
            } else {
              this.#prefetchRetryFrames.delete(task.index);
              this.#retryAfter.delete(task.index);
            }
          }
        }

        task.resolve(isCurrent ? resource : undefined);
      }).catch(() => task.resolve()).finally(() => {
        if (task.generation !== this.#loadGeneration) return;
        this.#activeLoads = Math.max(0, this.#activeLoads - 1);
        this.#releaseDownload(task);
        if (this.#loadTasks.get(task.index) === task) this.#loadTasks.delete(task.index);
        this.#updateLoadDiagnostics();
        this.#drainDecodeQueue();
        this.#drainLoadQueue();
      });
    }
  }

  #outputDimensions() {
    return {
      height: this.#outputHeight,
      scale: this.#outputScale,
      width: this.#outputWidth
    };
  }

  #updateOutputMetrics() {
    if (!this.#canvas) return;

    const pixelRatio = this.#outputPixelRatio;
    const width = Math.max(1, Math.round(this.#canvas.clientWidth * pixelRatio));
    const height = Math.max(1, Math.round(this.#canvas.clientHeight * pixelRatio));
    let outputWidth = width;
    let outputHeight = height;
    let outputScale = 1;
    if (!this.#isMobile && width * height > this.#renderPixelLimit) {
      outputScale = Math.sqrt(this.#renderPixelLimit / (width * height));
      outputWidth = Math.max(1, Math.round(width * outputScale));
      outputHeight = Math.max(1, Math.round(height * outputScale));
    }

    this.#outputWidth = outputWidth;
    this.#outputHeight = outputHeight;
    this.#outputScale = outputScale;
    this.#isTabletPortrait = !this.#isMobile
      && window.matchMedia('(min-width: 768px) and (orientation: portrait)').matches;

    this.#resolveCacheLimit();

    this.dataset.frameCacheLimit = String(this.#resolvedCacheLimit);
    this.dataset.frameOutputHeight = String(outputHeight);
    this.dataset.frameOutputWidth = String(outputWidth);
    this.dataset.frameRenderScale = outputScale.toFixed(3);
  }

  get #windowLimit() {
    return this.#isMobile ? SCROLL_KEYFRAMES_MOBILE_CACHE_LIMIT : SCROLL_KEYFRAMES_DESKTOP_MAX_CACHE_LIMIT;
  }

  #resolveCacheLimit() {
    const width = this.#frameSourceWidth || this.#outputWidth;
    const sourceWidth = Number(this.dataset.desktopFrameWidth) || 3840;
    const sourceHeight = Number(this.dataset.desktopFrameHeight) || 2160;
    const frameBytes = width * Math.ceil(width * sourceHeight / sourceWidth) * 4;
    this.#resolvedCacheLimit = this.#isMobile ? SCROLL_KEYFRAMES_MOBILE_CACHE_LIMIT : Math.max(
      this.#windowLimit,
      Math.min(SCROLL_KEYFRAMES_DESKTOP_RETENTION_LIMIT, Math.floor(SCROLL_KEYFRAMES_DESKTOP_CACHE_BYTES / frameBytes))
    );
    this.dataset.frameCacheLimit = String(this.#resolvedCacheLimit);
    this.dataset.frameWindowLimit = String(this.#windowLimit);
  }

  #releaseDownload(task) {
    if (task.downloadReleased || task.generation !== this.#loadGeneration) return;
    task.downloadReleased = true;
    this.#activeDownloads = Math.max(0, this.#activeDownloads - 1);
    this.#drainLoadQueue();
  }

  #waitForDecode(task) {
    return new Promise((resolve) => {
      task.resolveDecode = resolve;
      this.#decodeQueue.push(task);
      this.#drainDecodeQueue();
    });
  }

  #drainDecodeQueue() {
    this.#decodeQueue = this.#decodeQueue.filter((task) => {
      if (!task.cancelled && !task.finished && task.generation === this.#loadGeneration) return true;
      task.resolveDecode?.(false);
      return false;
    });
    this.#decodeQueue.sort((a, b) => (
      Number(b.index === this.#frameIndex) - Number(a.index === this.#frameIndex)
      || Math.abs(a.index - this.#frameIndex) - Math.abs(b.index - this.#frameIndex)
    ));
    while (this.#activeDecodes.size < 2 && this.#decodeQueue.length) {
      const task = this.#decodeQueue[0];
      // Only one speculative decode; reserve the other slot for the target.
      if (this.#activeDecodes.size && task.index !== this.#frameIndex) break;
      this.#decodeQueue.shift();
      task.decoding = true;
      this.#activeDecodes.add(task);
      task.resolveDecode(true);
      task.resolveDecode = undefined;
    }
  }

  #performLoad(task) {
    if (task.cancelled || task.generation !== this.#loadGeneration) return Promise.resolve();
    return this.#loadImageResource(this.#urlForFrame(task.index), task);
  }

  #startProgressivePrefetch(generation) {
    const start = () => {
      if (generation !== this.#loadGeneration || !this.isConnected) return;
      this.dataset.framePrefetchState = 'running';
      this.#schedulePrefetchBatch(generation);
    };

    if (document.readyState === 'complete') {
      start();
      return;
    }

    window.addEventListener('load', start, {
      once: true,
      signal: this.#abortController?.signal
    });
  }

  #schedulePrefetchBatch(generation) {
    this.#cancelPrefetchSchedule();
    const run = () => {
      this.#prefetchIdleCallback = undefined;
      this.#prefetchTimer = undefined;
      this.#prefetchNextBatch(generation);
    };

    if (document.visibilityState === 'hidden') {
      this.#prefetchTimer = window.setTimeout(() => this.#schedulePrefetchBatch(generation), 1000);
    } else if (typeof window.requestIdleCallback === 'function') {
      this.#prefetchIdleCallback = window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      this.#prefetchTimer = window.setTimeout(run, 80);
    }
  }

  #prefetchNextBatch(generation) {
    if (generation !== this.#loadGeneration || !this.isConnected) return;

    const availableSlots = this.#loadConcurrency - this.#activeDownloads;
    if (
      performance.now() - this.#lastFrameRequestAt < 200
      || this.#loadQueue.some((task) => !task.prefetch)
      || availableSlots <= 0
      || this.#decodeQueue.length > 0
    ) {
      this.#prefetchTimer = window.setTimeout(() => this.#schedulePrefetchBatch(generation), 200);
      return;
    }

    const tasks = [];
    // Fill unused slots while idle; a new target cancels out-of-window network
    // prefetches and temporarily pauses background batches. Total concurrency is unchanged.
    const batchSize = availableSlots;
    while (
      (this.#prefetchRetryFrames.size > 0 || this.#prefetchCursor < this.#frameCount)
      && tasks.length < batchSize
    ) {
      const retryFrame = [...this.#prefetchRetryFrames].find((index) => (
        performance.now() >= (this.#retryAfter.get(index) || 0)
      ));
      if (retryFrame === undefined && this.#prefetchCursor >= this.#frameCount) break;
      const index = retryFrame ?? this.#prefetchCursor;
      if (retryFrame !== undefined) {
        this.#prefetchRetryFrames.delete(retryFrame);
      } else {
        this.#prefetchCursor += 1;
      }
      if (this.#prefetchedFrames.has(index)) continue;
      tasks.push(this.#loadFrame(index, 'low', true));
    }

    this.dataset.framePrefetchProgress = String(this.#prefetchCursor);
    if (tasks.length === 0) {
      if (this.#prefetchRetryFrames.size) {
        this.#prefetchTimer = window.setTimeout(() => this.#schedulePrefetchBatch(generation), 200);
        return;
      }
      this.dataset.framePrefetchState = this.#prefetchedFrames.size === this.#frameCount ? 'complete' : 'partial';
      return;
    }

    Promise.allSettled(tasks).then(() => {
      if (generation === this.#loadGeneration) this.#schedulePrefetchBatch(generation);
    });
  }

  #cancelPrefetchSchedule() {
    if (this.#prefetchIdleCallback !== undefined && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(this.#prefetchIdleCallback);
    }
    if (this.#prefetchTimer !== undefined) window.clearTimeout(this.#prefetchTimer);
    this.#prefetchIdleCallback = undefined;
    this.#prefetchTimer = undefined;
  }

  #loadImageResource(url, task) {
    return new Promise((resolve) => {
      const image = new Image();
      let settled = false;
      const timeout = window.setTimeout(() => {
        image.removeAttribute('src');
        finish();
      }, SCROLL_KEYFRAMES_LOAD_TIMEOUT);
      const finish = (resource) => {
        if (settled) return;
        settled = true;
        task.finished = true;
        window.clearTimeout(timeout);
        task.resolveDecode?.(false);
        task.resolveDecode = undefined;
        task.cancelled ||= !resource && task.generation !== this.#loadGeneration;
        task.cancelImage = undefined;
        task.setPriority = undefined;
        image.onload = null;
        image.onerror = null;
        resolve(resource);
      };

      task.cancelImage = () => {
        image.removeAttribute('src');
        finish();
      };
      image.decoding = 'async';
      task.setPriority = (priority) => {
        image.fetchPriority = priority === 'high' ? 'high' : priority === 'low' ? 'low' : 'auto';
      };
      task.setPriority(task.priority);
      image.onload = async () => {
        this.#releaseDownload(task);
        const decoded = !task.prefetch || this.#isNearbyFrame(task.index);
        if (decoded && typeof image.decode === 'function') {
          const canDecode = await this.#waitForDecode(task);
          if (!canDecode || settled) {
            task.decoding = false;
            this.#activeDecodes.delete(task);
            this.#drainDecodeQueue();
            return;
          }
          try {
            await image.decode();
          } catch (error) {
            image.removeAttribute('src');
            return finish();
          } finally {
            task.decoding = false;
            this.#activeDecodes.delete(task);
            this.#drainDecodeQueue();
          }
        }
        if (task.cancelled || task.generation !== this.#loadGeneration) return finish();
        finish({
          close: () => image.removeAttribute('src'),
          decoded,
          height: image.naturalHeight,
          source: image,
          type: 'image',
          width: image.naturalWidth
        });
      };
      image.onerror = () => finish();
      image.src = url;
    });
  }

  /**
   * Frame 0 keeps the digits before the extension. Later frames add the index
   * and keep that width, so 0001.webp becomes 0002 and 00000.webp becomes 00113.
   * @param {number} index
   */
  #urlForFrame(index) {
    const url = new URL(this.#frameUrl, document.baseURI);
    url.pathname = url.pathname.replace(/(\d+)(?=\.[^./]+$)/, (token) => {
      return String(Number(token) + index).padStart(token.length, '0');
    });
    if (this.#frameSourceWidth > 0) url.searchParams.set('width', String(this.#frameSourceWidth));
    return url.href;
  }

  get #cacheLimit() {
    return this.#resolvedCacheLimit;
  }

  get #loadConcurrency() {
    return this.#isMobile
      ? SCROLL_KEYFRAMES_MOBILE_LOAD_CONCURRENCY
      : SCROLL_KEYFRAMES_DESKTOP_LOAD_CONCURRENCY;
  }

  get #outputPixelRatio() {
    return Math.min(window.devicePixelRatio || 1, this.#isMobile ? 1.25 : 1);
  }

  #trimCache(center, limit) {
    if (this.#images.size <= limit) return;

    [...this.#images.keys()]
      .sort((a, b) => (
        Number(this.#isNearbyFrame(a)) - Number(this.#isNearbyFrame(b))
        || Math.abs(b - center) - Math.abs(a - center)
      ))
      .slice(0, this.#images.size - limit)
      .forEach((key) => {
        const resource = this.#images.get(key);
        if (resource !== this.#lastDrawnResource) resource?.close?.();
        this.#images.delete(key);
      });
    this.#updateCacheDiagnostics();
  }

  #updateCacheDiagnostics() {
    this.#queueFrameDiagnostics();
  }

  #updateLoadDiagnostics() {
    this.#queueFrameDiagnostics();
  }

  #queueFrameDiagnostics() {
    if (!this.isConnected || this.#diagnosticTimer !== undefined) return;
    this.#diagnosticTimer = window.setTimeout(() => {
      this.#diagnosticTimer = undefined;
      this.dataset.frameActiveLoads = String(this.#activeLoads);
      this.dataset.frameActiveDownloads = String(this.#activeDownloads);
      this.dataset.frameActiveDecodes = String(this.#activeDecodes.size);
      this.dataset.frameCacheSize = String(this.#images.size);
      this.dataset.framePrefetchedCount = String(this.#prefetchedFrames.size);
      this.dataset.frameQueuedLoads = String(this.#loadQueue.length);
      if (this.#lastRequestedFrame >= 0) this.dataset.frameTarget = String(this.#lastRequestedFrame);
      if (this.#lastDrawnFrame >= 0) this.dataset.frameRendered = String(this.#lastDrawnFrame);
    }, 100);
  }

  #resetFrameLoader() {
    this.#loadGeneration += 1;
    this.#cancelPrefetchSchedule();
    this.#prefetchCursor = 0;
    this.#prefetchedFrames.clear();
    this.#prefetchRetryFrames.clear();
    this.#loadFailures.clear();
    this.#retryAfter.clear();
    this.#decodeQueue.forEach((task) => task.resolveDecode?.(false));
    this.#decodeQueue.length = 0;
    this.#loadQueue.length = 0;
    this.#loadTasks.forEach((task) => {
      task.cancelled = true;
      task.cancelImage?.();
      task.resolve?.();
    });
    this.#loadTasks.clear();
    this.#activeLoads = 0;
    this.#activeDownloads = 0;
    this.#images.forEach((resource) => resource.close?.());
    this.#images.clear();
    this.#lastDrawnResource?.close?.();
    this.#lastDrawnFrame = -1;
    this.#lastDrawnResource = undefined;
    this.#lastRequestedFrame = -1;
    delete this.dataset.frameDecoder;
    delete this.dataset.frameDecodedHeight;
    delete this.dataset.frameDecodedWidth;
    delete this.dataset.frameCacheLimit;
    delete this.dataset.frameDroppedLoads;
    delete this.dataset.framePrefetchProgress;
    delete this.dataset.framePrefetchState;
    delete this.dataset.framePrefetchedCount;
    delete this.dataset.framePerformanceTier;
    delete this.dataset.frameOutputHeight;
    delete this.dataset.frameOutputWidth;
    delete this.dataset.frameRendered;
    delete this.dataset.frameRenderScale;
    delete this.dataset.frameTarget;
    delete this.dataset.frameWorker;
    this.#updateCacheDiagnostics();
    this.#updateLoadDiagnostics();
  }

  #requestScrollTriggerRefresh(ScrollTrigger) {
    if (typeof window.requestScrollTriggerRefresh === 'function') {
      window.requestScrollTriggerRefresh({ delay: 0 });
      return;
    }

    requestAnimationFrame(() => {
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    });
  }

  #queueRender = () => {
    if (this.#renderFrame || this.#renderTicker) return;
    if (window.gsap?.ticker) {
      this.#renderTicker = window.gsap.ticker;
      this.#renderTicker.add(this.#flushRender, true);
    } else {
      this.#renderFrame = requestAnimationFrame(this.#flushRender);
    }
  };

  #flushRender = () => {
    this.#renderFrame = undefined;
    this.#renderTicker = undefined;
    if (this.isConnected) this.#drawFrame();
  };

  #drawFrame() {
    if (!this.#canvas || !this.#context) return;

    const frame = this.#nearestLoadedImage();
    if (!frame) return;

    const output = this.#outputDimensions();
    if (!output) return;
    const outputWidth = output.width;
    const outputHeight = output.height;
    const canvasNeedsResize = this.#canvas.width !== outputWidth || this.#canvas.height !== outputHeight;

    if (
      frame.index === this.#lastDrawnFrame
      && frame.resource === this.#lastDrawnResource
      && !canvasNeedsResize
    ) return;

    if (canvasNeedsResize) {
      this.#canvas.width = outputWidth;
      this.#canvas.height = outputHeight;
    }

    const coverScale = Math.max(outputWidth / frame.resource.width, outputHeight / frame.resource.height);
    const scale = this.#isTabletPortrait ? coverScale * 0.72 : coverScale;
    const drawWidth = frame.resource.width * scale;
    const drawHeight = frame.resource.height * scale;
    const x = ((outputWidth - drawWidth) / 2) - (this.#isTabletPortrait ? outputWidth * 0.07 : 0);
    const y = (outputHeight - drawHeight) / 2;

    if (this.#isTabletPortrait) this.#context.clearRect(0, 0, outputWidth, outputHeight);
    this.#context.drawImage(frame.resource.source, x, y, drawWidth, drawHeight);
    const previousResource = this.#lastDrawnResource;
    const previousIndex = this.#lastDrawnFrame;
    this.#lastDrawnFrame = frame.index;
    this.#lastDrawnResource = frame.resource;
    if (previousResource && previousResource !== frame.resource && this.#images.get(previousIndex) !== previousResource) {
      previousResource.close?.();
    }
    this.#queueFrameDiagnostics();
    this.classList.add('has-rendered-frame');
  }

  #nearestLoadedImage() {
    const exactImage = this.#images.get(this.#frameIndex);
    if (exactImage) return { index: this.#frameIndex, resource: exactImage };

    let nearest;
    let nearestIndex = -1;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.#images.forEach((resource, index) => {
      const distance = Math.abs(index - this.#frameIndex);
      if (distance < nearestDistance) {
        nearest = resource;
        nearestIndex = index;
        nearestDistance = distance;
      }
    });

    return nearest ? { index: nearestIndex, resource: nearest } : undefined;
  }

  #syncCallout(progress) {
    if (progress >= 0.12 && progress <= 0.41) {
      this.#setActiveCallout('awd');
    } else if (progress >= 0.47 && progress <= 0.82) {
      this.#setActiveCallout('suspension');
    } else {
      this.#setActiveCallout(null);
    }
  }

  #setActiveCallout(name) {
    if (name === this.#lastCallout) return;

    this.#lastCallout = name;
    this.querySelectorAll('[data-keyframe-callout]').forEach((callout) => {
      callout.setAttribute('aria-hidden', String(callout.dataset.keyframeCallout !== name));
    });

    const status = this.querySelector('[data-keyframe-status]');
    const active = name ? this.querySelector(`[data-keyframe-callout="${name}"]`) : null;
    if (status) status.textContent = active?.querySelector('.scroll-keyframes__callout-title')?.textContent?.trim() || '';
  }

  #showStaticCallouts() {
    this.#lastCallout = undefined;
    this.querySelectorAll('[data-keyframe-callout]').forEach((callout) => callout.removeAttribute('aria-hidden'));
    const status = this.querySelector('[data-keyframe-status]');
    if (status) status.textContent = '';
  }

  #destroyMotion() {
    this.#matchMedia?.revert();
    this.#matchMedia = undefined;
    this.#timeline?.kill();
    this.#timeline = undefined;
    this.#motionGeneration += 1;
    this.#stageHeight = 0;
    this.style.removeProperty('--scroll-keyframes-mobile-stage-size');
    this.classList.remove('is-enhanced', 'is-reduced-motion', 'has-rendered-frame');
  }
}

if (!customElements.get('scroll-keyframes')) {
  customElements.define('scroll-keyframes', ScrollKeyframes);
}
